/**
 * The date already sitting in the sentence.
 *
 * A persona captured 21 things the way they actually would — terse, no metadata — and eight of
 * them carried a date in the words: "book the ferry next Tuesday", "bins out tonight", "cancel
 * the gym trial in 5 days". All 21 landed with no date at all, because the only way to set one is
 * two adjacent unlabelled date pickers behind a task's detail panel. The owner's own account
 * agrees: ten tasks, zero due dates.
 *
 * That absence is not cosmetic. `rankCandidate` bands the day's shortlist by `scheduledDate` and
 * `dueAt`, so with neither set every task scores identically and the shortlist is reverse capture
 * order — "bins out tonight" sat thirteenth on the evening it mattered.
 *
 * Three rules this follows, all of them about not being clever:
 *
 * 1. **It suggests, never applies.** One of that persona's eight was "chase the invoice from
 *    June" — retrospective, and June is not when it is due. A parser that silently writes dates
 *    would be wrong often enough to have to be distrusted, and then the whole feature is a tax. A
 *    chip you can ignore costs nothing when it is wrong.
 * 2. **It writes `scheduledDate`, not `dueAt`.** That is the field the day planner ranks on, and
 *    choosing between two unlabelled pickers is exactly the decision this exists to remove.
 * 3. **It has a fixed grammar.** No inference, no fuzzy matching. If it does not recognise the
 *    phrase it says nothing, which is always a safe answer.
 */

export interface ParsedWhen {
  /** ISO day, in the caller's local reckoning. */
  date: string;
  /** The matched text, so the caller can strip it from the title. */
  matched: string;
}

const WEEKDAYS: Record<string, number> = {
  monday: 0,
  mon: 0,
  tuesday: 1,
  tue: 1,
  tues: 1,
  wednesday: 2,
  wed: 2,
  thursday: 3,
  thu: 3,
  thurs: 3,
  friday: 4,
  fri: 4,
  saturday: 5,
  sat: 5,
  sunday: 6,
  sun: 6,
};

const MONTHS: Record<string, number> = {
  january: 0,
  jan: 0,
  february: 1,
  feb: 1,
  march: 2,
  mar: 2,
  april: 3,
  apr: 3,
  may: 4,
  june: 5,
  jun: 5,
  july: 6,
  jul: 6,
  august: 7,
  aug: 7,
  september: 8,
  sep: 8,
  sept: 8,
  october: 9,
  oct: 9,
  november: 10,
  nov: 10,
  december: 11,
  dec: 11,
};

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const plus = (from: Date, days: number): Date => {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  return d;
};

/** 0 = Monday … 6 = Sunday, matching the rest of this codebase rather than `Date.getDay`. */
const weekdayOf = (d: Date): number => (d.getDay() + 6) % 7;

/**
 * `today` anchors every relative phrase, and it is the *account's* day.
 *
 * The default is the browser's clock and is kept only for the unit tests, which pass their own
 * anchor anyway. `Capture` hands in noon of `useAccountDay().today` — noon so the arithmetic below
 * stays far from either midnight — because "tomorrow" typed at 01:00 in Warsaw on a laptop still
 * on UTC would otherwise resolve to the day the account is already having.
 */
export function parseWhen(text: string, today: Date = new Date()): ParsedWhen | null {
  const lower = text.toLowerCase();

  const on = (re: RegExp, resolve: (m: RegExpMatchArray) => Date | null): ParsedWhen | null => {
    const m = lower.match(re);
    if (!m) return null;
    const date = resolve(m);
    return date ? { date: iso(date), matched: m[0].trim() } : null;
  };

  return (
    // "tomorrow", "tonight", "today" — the most common and the least ambiguous.
    on(/\b(today|tonight)\b/, () => today) ??
    on(/\btomorrow\b/, () => plus(today, 1)) ??
    // "in 3 days", "in 2 weeks"
    on(/\bin (\d{1,3}) (day|days|week|weeks)\b/, (m) =>
      plus(today, Number(m[1]) * (m[2]!.startsWith('week') ? 7 : 1)),
    ) ??
    /*
      "next tuesday" versus "tuesday".

      Bare "friday" means the coming Friday — today if it is Friday counts as *next* Friday, since
      somebody writing "call mum on Friday" on a Friday almost certainly means the one ahead.
      "next friday" adds a further week, which is what people mean by it in practice even though
      it is genuinely ambiguous; being consistently one reading is better than guessing per case,
      and the chip shows the resolved date so a wrong guess is visible before it is accepted.
    */
    on(/\bnext (\w+)\b/, (m) => {
      const target = WEEKDAYS[m[1]!];
      if (target === undefined) return null;
      const ahead = (target - weekdayOf(today) + 7) % 7 || 7;
      return plus(today, ahead + 7);
    }) ??
    on(/\b(?:on |this )?(\w+day|mon|tue|tues|wed|thu|thurs|fri|sat|sun)\b/, (m) => {
      const target = WEEKDAYS[m[1]!];
      if (target === undefined) return null;
      const ahead = (target - weekdayOf(today) + 7) % 7 || 7;
      return plus(today, ahead);
    }) ??
    // "3 sept", "sept 3", "3 september"
    on(/\b(\d{1,2})(?:st|nd|rd|th)? (\w+)\b/, (m) => resolveMonthDay(Number(m[1]), m[2]!, today)) ??
    on(/\b(\w+) (\d{1,2})(?:st|nd|rd|th)?\b/, (m) => resolveMonthDay(Number(m[2]), m[1]!, today))
  );
}

/** A day and a month name, resolved into the next occurrence of that date. */
function resolveMonthDay(day: number, monthWord: string, today: Date): Date | null {
  const month = MONTHS[monthWord];
  if (month === undefined || day < 1 || day > 31) return null;

  const candidate = new Date(today.getFullYear(), month, day);
  if (candidate.getMonth() !== month) return null; // 31 February and friends.
  // A date already gone means next year — "book flights 3 jan" in December.
  if (iso(candidate) < iso(today)) candidate.setFullYear(candidate.getFullYear() + 1);
  return candidate;
}

/** The title with the matched phrase removed, tidied. */
export function stripWhen(text: string, matched: string): string {
  const cleaned = text
    .replace(new RegExp(`\\b${matched.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i'), ' ')
    // The connecting word is usually left dangling: "call mum on" → "call mum".
    .replace(/\s+(on|by|at|this|next)\s*$/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  // Never hand back an empty title; if the phrase was the whole thing, keep the original.
  return cleaned === '' ? text.trim() : cleaned;
}
